import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, fromEvent, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    scan,
    shareReplay,
    startWith,
    switchMap,
    takeUntil
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ScrollService } from '../../../core/common/services/scroll.service';
import { DropdownDirection as DroppanelOpenDirection } from '../../../form-controls/entities/dropdown-direction.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ClearMarginModifiers } from '../../entities/clear-margin-modifier.entity';
import { fixElementRightToParentRight } from '../../helpers/dom-positioning.helper';
import { DroppanelStyle } from './entities/droppanel-style.entity';

export * from './entities/droppanel-style.entity';

@Component({
    standalone: true,
    selector: 'sh-droppanel',
    templateUrl: './droppanel.component.html',
    imports: [CommonModule]
})
export class ShDroppanelComponent extends BaseContainer implements OnInit, OnDestroy {
    readonly intersectionRatioObserver$: Subject<number> = new Subject<number>();
    public readonly openDirection$: BehaviorSubject<DroppanelOpenDirection> =
        new BehaviorSubject<DroppanelOpenDirection>(DroppanelOpenDirection.Below);
    public calculatedOpenDirection$: Observable<DroppanelOpenDirection>;
    readonly MIN_HEIGHT: string = '40px';

    private intersectionObserver!: IntersectionObserver;
    private droppanelList!: ElementRef<HTMLElement>;

    public maxHeight$!: Observable<string>;

    @Input() inOverflownContainer = false;
    @Input() height = 300;
    @Input() set openDirection(openDirection: DroppanelOpenDirection) {
        this.openDirection$.next(openDirection);
    }
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;
    @Input() clearMarginModifiers?: ClearMarginModifiers;
    @Input() focusMode: boolean = false;

    @Input() isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Input() hasOptions$: Observable<boolean> = new BehaviorSubject<boolean>(false);
    @Input() limitHeightToViewport$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Input() loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    @ViewChild('droppanelList') set droppanelListElement(droppanelListElement: ElementRef<HTMLElement>) {
        if (droppanelListElement) {
            this.dropPanelList$.next(droppanelListElement);
        }
    }

    @ViewChild('toggleElement') droppanelToggle!: ElementRef<HTMLElement>;

    dropPanelList$: ReplaySubject<ElementRef<HTMLElement>> = new ReplaySubject(1);

    @ViewChild('droppanel') droppanel?: ElementRef<HTMLElement>;

    constructor(private readonly scrollService: ScrollService) {
        super();
    }

    ngOnInit(): void {
        this.adjustDropdownToLoadingState();
        this.initSubscribers();
        this.initIntersectionObserver();
        this.initMaxHeightStream();
        this.initOpenDirectionStream();

        this.initIntersectionObserverForDroppanel();
    }

    private initSubscribers(): void {
        this.initScrollToTopSubscriber();
        this.initAdjustDroppanelPositionSubscriber();
    }

    private initIntersectionObserverForDroppanel(): void {
        combineLatest([this.hasOptions$, this.dropPanelList$])
            .pipe(
                filter(([hasOptions]) => hasOptions),
                takeUntil(this.destroy$)
            )
            .subscribe(([, elemn]) => {
                if (this.intersectionObserver) {
                    if (this.droppanelList) {
                        this.intersectionObserver.unobserve(this.droppanelList.nativeElement);
                    }

                    this.intersectionObserver.observe(elemn.nativeElement);
                }
                this.droppanelList = elemn;
            });
    }

    private initScrollToTopSubscriber(): void {
        this.isOpen$
            .pipe(
                distinctUntilChanged(),
                filter(isOpen => isOpen === false),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.scrollDropdownToTop());
    }

    private initAdjustDroppanelPositionSubscriber(): void {
        // capture: true needed for capturing scroll event inside overflow containers
        const didScroll$: Observable<boolean> = fromEvent(window, 'scroll', { capture: true }).pipe(
            mapTo(true),
            startWith(true)
        );

        combineLatest([this.isOpen$, didScroll$])
            .pipe(
                filter(([isOpen]) => isOpen),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.droppanel) {
                    const bottomY: number = this.droppanelToggle?.nativeElement.getBoundingClientRect().bottom || 0;
                    const width: number = this.droppanelToggle?.nativeElement.getBoundingClientRect().width || 0;
                    this.droppanel.nativeElement.style.width = `${width}px`;
                    if (this.inOverflownContainer) {
                        this.droppanel.nativeElement.style.top = `${bottomY}px`;

                        const boundingClientRectOfToggle: DOMRect | undefined =
                            this.droppanelToggle?.nativeElement.getBoundingClientRect();

                        fixElementRightToParentRight(this.droppanel.nativeElement, boundingClientRectOfToggle);
                    }
                }
            });
    }

    private initMaxHeightStream(): void {
        this.maxHeight$ = this.limitHeightToViewport$.pipe(
            switchMap(limitHeight =>
                limitHeight ? this.getHeightBasedOnIntersectionRatio$() : of(this.heightOfDropdown)
            ),
            shareReplay()
        );
    }

    private initOpenDirectionStream(): void {
        this.calculatedOpenDirection$ = combineLatest([this.isOpen$, this.limitHeightToViewport$]).pipe(
            distinctUntilChanged(),
            filter(([isOpen]) => isOpen),
            switchMap(([, limitHeight]) =>
                limitHeight
                    ? this.openDirection$
                    : this.openDirection$.pipe(map(defaultDirection => this.calculateOpenDirection(defaultDirection)))
            )
        );
    }

    private calculateOpenDirection(defaultDirection: DroppanelOpenDirection): DroppanelOpenDirection {
        const dropdownToggle: HTMLElement = this.droppanelToggle.nativeElement;
        const toggleRect: DOMRect = dropdownToggle.getBoundingClientRect();

        // Calculate available space above and below the dropdown
        const spaceBelow: number = window.innerHeight - toggleRect.bottom;
        const spaceAbove: number = toggleRect.top;

        // Determine if the dropdown should open up or down based on the default direction
        switch (defaultDirection) {
            case DroppanelOpenDirection.Below:
                return spaceBelow < this.height && spaceAbove > this.height
                    ? DroppanelOpenDirection.Above
                    : DroppanelOpenDirection.Below;
            case DroppanelOpenDirection.Above:
                return spaceAbove < this.height && spaceBelow > this.height
                    ? DroppanelOpenDirection.Below
                    : DroppanelOpenDirection.Above;
            case DroppanelOpenDirection.BelowLeft:
                return spaceBelow < this.height && spaceAbove > this.height
                    ? DroppanelOpenDirection.AboveLeft
                    : DroppanelOpenDirection.BelowLeft;
            case DroppanelOpenDirection.AboveLeft:
                return spaceAbove < this.height && spaceBelow > this.height
                    ? DroppanelOpenDirection.BelowLeft
                    : DroppanelOpenDirection.AboveLeft;
            default:
                return defaultDirection;
        }
    }

    private initIntersectionObserver(): void {
        if (!this.isIntersectionObserverSupported()) {
            return;
        }

        this.intersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                this.intersectionRatioObserver$.next(entry.intersectionRatio);
            });
        }, {});
    }

    private getHeightBasedOnIntersectionRatio$(): Observable<string> {
        return this.isOpen$.pipe(
            filter(isOpen => isOpen),
            switchMap(() =>
                this.intersectionRatioObserver$.pipe(
                    startWith(1),
                    filter(ratio => ratio > 0),
                    distinctUntilChanged(),
                    // Subtract from ratio to add some margin
                    scan((prev, ratio) => Math.min(prev * (ratio - 0.05), this.height), this.height),
                    startWith(this.height),
                    map(height => `${height}px`)
                )
            ),
            takeUntil(this.destroy$)
        );
    }

    private isIntersectionObserverSupported(): boolean {
        return (
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype
        );
    }

    private scrollDropdownToTop(): void {
        if (this.droppanelList) {
            this.droppanelList.nativeElement.scrollTop = 0;
        }
    }

    private adjustDropdownToLoadingState(): void {
        this.loading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading: boolean) => {
            if (isLoading) {
                //this.scrollDropdownToTop();
            }
        });
    }

    public get heightOfDropdown(): string {
        return this.height + 'px';
    }

    ngOnDestroy(): void {
        this.intersectionRatioObserver$.complete();
        this.loading$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
